import PropTypes from "prop-types";
import * as React from "react";
import { capitalizeWord } from "../../../../util";
import { Button, Dialog, Switch } from "../../../../themeV2";

async function setAttribute({ influencerId, attributeKey, attributeValue }) {
  try {
    const res = await window.fetch(`/api/influencer/${influencerId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [attributeKey]: attributeValue }),
    });
    return res.ok;
  } catch (err) {
    console.error(err);
    return false;
  }
}

function readable(attributeKey) {
  const words = attributeKey.split("_");
  return words.map(capitalizeWord).join(" ");
}

const ToggleAttribute = React.memo(function ToggleAttribute({
  attributeKey,
  attributeValue,
  displayName,
  influencerId,
}) {
  const [hasAttribute, setHasAttribute] = React.useState(
    Boolean(attributeValue),
  );
  const [pendingValue, setPendingValue] = React.useState(null);
  const [errorText, setErrorText] = React.useState("");
  const readableAttributeKey = displayName ?? readable(attributeKey);

  const onChange = React.useCallback((newValue) => {
    setErrorText("");
    setPendingValue(newValue);
  }, []);

  const handleConfirm = React.useCallback(async () => {
    const isSuccess = await setAttribute({
      influencerId,
      attributeKey,
      attributeValue: pendingValue,
    });
    if (isSuccess) {
      setHasAttribute(pendingValue);
    } else {
      setErrorText(
        `Unable to update status of attribute ${readableAttributeKey}`,
      );
    }
    setPendingValue(null);
  }, [attributeKey, influencerId, pendingValue, readableAttributeKey]);

  return (
    <>
      <Switch
        checked={hasAttribute}
        name={attributeKey}
        onCheckedChange={onChange}
        label={readableAttributeKey}
      />
      {errorText && (
        <p
          style={{
            font: "var(--type-body-md)",
            letterSpacing: "var(--tracking-body-md)",
            color: "var(--md-error)",
            margin: "var(--space-1) 0 0",
          }}
        >
          {errorText}
        </p>
      )}

      <Dialog.Root
        alert
        open={pendingValue != null}
        onOpenChange={(open) => {
          if (!open) setPendingValue(null);
        }}
      >
        <Dialog.Popup size="sm">
          <Dialog.Title>Confirm change</Dialog.Title>
          <Dialog.Description>
            Are you sure you want to set shop attribute {readableAttributeKey}{" "}
            to {String(pendingValue)}?
          </Dialog.Description>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "var(--space-2)",
              marginBlockStart: "var(--space-4)",
            }}
          >
            <Button variant="outlined" onClick={() => setPendingValue(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </div>
        </Dialog.Popup>
      </Dialog.Root>
    </>
  );
});

ToggleAttribute.propTypes = {
  attributeKey: PropTypes.string.isRequired,
  attributeValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.number])
    .isRequired,
  displayName: PropTypes.string,
  influencerId: PropTypes.number.isRequired,
};

export default ToggleAttribute;
